/**
 * DateInput utils
 */

import {
  useMemo,
} from 'react';
import isValid from 'date-fns/isValid';
import dateFnsParse from 'date-fns/parse';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import {
  DateValidationProps,
  ParsableDate,
  FormatCallbackObjProps,
  FormatDisplayValueProps
} from './dateInput.types';

export const validateDate = (
  value: string | number | Date,
  {
    invalidDateMessage,
    minDate,
    minDateMessage,
    maxDate,
    maxDateMessage,
  }: DateValidationProps
) => {

  const date = new Date(value);

  if (value === null) {
    return null;
  }

  switch (true) {
    case !isValid(value):
      return invalidDateMessage;

    case Boolean(minDate && isBefore(date, minDate)):
      return minDateMessage || invalidDateMessage;

    case Boolean(maxDate && isAfter(date, maxDate)):
      return maxDateMessage || invalidDateMessage;

    default:
      return null;
  }
};

export const useParsedDate = (possiblyUnparsedValue: ParsableDate) => useMemo(
  () => typeof possiblyUnparsedValue === 'undefined' ? undefined : new Date(possiblyUnparsedValue)!,
  [possiblyUnparsedValue]
);

export const formatCallbackObj = ({
  date,
  outputFormat,
  enteredString,
  valid,
}: FormatCallbackObjProps) => {

  const string = isValid(date) ? format(date, outputFormat) : enteredString || null;

  return ({
    date,
    string,
    valid,
  });

};

// converts local Date to UTC Date
export const utcDate = (date: Date) => new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);

// baseDate to be used in dateFnsParse.
export const baseDate = (() => {
  // We check validation on each segment individually.
  // Using a date with month that has 31 days as since new Date() can return false days.
  // e.g. Current date is a day in Feb.
  const date = new Date(0);
  return utcDate(date);
})();

export const formatDisplayValue = ({
  value,
  dateFormat,
  skip,
}: FormatDisplayValueProps) => {

  if (!value) {

    return '';

  }

  if (skip) {
    return value;
  }

  const date = dateFnsParse(value, dateFormat, baseDate);
  const dateOnly = utcDate(date);
  const formattedDate = isValid(dateOnly) ? format(dateOnly, dateFormat) : '';

  return formattedDate;

};
